import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Política de Privacidad — Rescate de Pareja Post-Crisis

Última actualización: Abril 2026

1. Información que recopilamos
Recopilamos únicamente la información necesaria para procesar tu compra: nombre, dirección de correo electrónico y datos de pago. No almacenamos datos de tarjetas de crédito directamente; estos son procesados por nuestro proveedor de pagos seguro.

2. Uso de la información
Tu información personal se utiliza exclusivamente para:
• Procesar y entregar tu compra digital
• Enviarte el material adquirido por correo electrónico
• Comunicarnos contigo en caso de problemas con tu pedido

3. Protección de datos
Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración o destrucción.

4. Compartir información
No vendemos, intercambiamos ni transferimos tu información personal a terceros. La única excepción son los proveedores de servicios que nos ayudan a operar nuestro sitio y procesar pagos, quienes están obligados a mantener la confidencialidad.

5. Cookies
Nuestro sitio puede utilizar cookies para mejorar la experiencia del usuario. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar algunas funcionalidades.

6. Tus derechos
Tenés derecho a acceder, rectificar o eliminar tus datos personales en cualquier momento. Para ejercer estos derechos, contactanos a través de los canales disponibles en nuestro sitio.

7. Cambios a esta política
Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios serán publicados en esta página con la fecha de actualización correspondiente.
`.trim();
const termsContent = `
Términos y Condiciones — Rescate de Pareja Post-Crisis

Última actualización: Abril 2026

1. Aceptación de los términos
Al adquirir y utilizar este producto digital, aceptás estos términos y condiciones en su totalidad. Si no estás de acuerdo, no realices la compra.

2. Descripción del producto
"Rescate de Pareja Post-Crisis" es un producto digital (eBook en formato PDF) que incluye guías, plantillas y herramientas para mejorar la comunicación en pareja. No constituye asesoramiento psicológico, terapéutico ni profesional.

3. Entrega del producto
El producto se entrega de forma digital e inmediata tras confirmar el pago. Recibirás un enlace de descarga y/o el material por correo electrónico.

4. Política de reembolso
Debido a la naturaleza digital del producto, no se aceptan devoluciones una vez realizada la descarga. Si tenés problemas técnicos para acceder al material, contactanos y te ayudaremos a resolverlo.

5. Uso permitido
El material es para uso personal y de tu pareja. Queda prohibida la reproducción, distribución, reventa o publicación del contenido, total o parcial, sin autorización expresa.

6. Limitación de responsabilidad
Este producto es una herramienta educativa y de autoayuda. No reemplaza la terapia profesional. Los resultados pueden variar según cada pareja y situación. No nos hacemos responsables por decisiones tomadas en base al contenido del material.

7. Situaciones de violencia
Este producto NO está diseñado para parejas en situaciones de violencia física o abuso. Si te encontrás en esa situación, te recomendamos buscar ayuda profesional inmediata. En Argentina podés llamar a la línea 144.

8. Propiedad intelectual
Todo el contenido del producto está protegido por derechos de autor. La compra otorga una licencia de uso personal, no transferible.

9. Modificaciones
Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigencia desde su publicación.

10. Legislación aplicable
Estos términos se rigen por las leyes de la República Argentina. Cualquier controversia será sometida a los tribunales competentes de la Ciudad Autónoma de Buenos Aires.
`.trim();
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="w-full bg-[#E8E4E1] py-12 border-t border-black/5 pb-24 md:pb-12" data-id="element-73">
        <div className="max-w-3xl mx-auto px-6" data-id="element-74">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6" data-id="element-75">
            <p className="text-[#6B7280] text-sm" data-id="element-76">
              © 2026 Rescate de Pareja Post-Crisis. Todos los derechos
              reservados.
            </p>
            <div className="flex items-center gap-6" data-id="element-77">
              <button onClick={() => setActiveModal('privacy')} className="text-[#6B7280] hover:text-[#2D2D2D] text-sm transition-colors" data-id="element-78">
                Política de Privacidad
              </button>
              <button onClick={() => setActiveModal('terms')} className="text-[#6B7280] hover:text-[#2D2D2D] text-sm transition-colors" data-id="element-79">
                Términos y Condiciones
              </button>
            </div>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-80">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-81">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25
        }} className="bg-[#FFFFFF] rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-82">
              <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-83">
                <h3 className="text-xl font-bold text-[#2D2D2D]" data-id="element-84">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors" data-id="element-85">
                  <XIcon className="w-5 h-5 text-[#2D2D2D]" data-id="element-86" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-87">
                <div className="prose prose-sm max-w-none" data-id="element-88">
                  {(activeModal === 'privacy' ? privacyContent : termsContent).split('\n').map((line, i) => {
                if (!line.trim()) return <div key={i} className="h-3" data-id="element-89" />;
                if (/^\d+\./.test(line.trim())) {
                  return <h4 key={i} className="text-lg font-semibold text-[#2D2D2D] mt-6 mb-2" data-id="element-90">
                            {line}
                          </h4>;
                }
                if (line.startsWith('Política') || line.startsWith('Términos')) {
                  return <h4 key={i} className="text-lg font-bold text-[#722F37] mb-1" data-id="element-91">
                            {line}
                          </h4>;
                }
                if (line.startsWith('Última')) {
                  return <p key={i} className="text-sm text-[#6B7280] mb-4" data-id="element-92">
                            {line}
                          </p>;
                }
                if (line.startsWith('•')) {
                  return <p key={i} className="text-[#334155] pl-4 mb-1" data-id="element-93">
                            {line}
                          </p>;
                }
                return <p key={i} className="text-[#334155] mb-2 leading-relaxed" data-id="element-94">
                          {line}
                        </p>;
              })}
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}